import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetModalProvider,
  BottomSheetScrollView,
  KEYBOARD_STATUS,
  useBottomSheetInternal,
} from '@gorhom/bottom-sheet';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Dimensions,
  Keyboard,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
} from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Alert } from '@components/Alert';
import { TeamSelect } from '@components/FormModals/TeamSelect';
import { FloatingHeader } from '@components/Headers/FloatingHeader';
import { BottomSheetBackdrop } from '@components/Modals/BottomSheetBackdrop';
import { runProjectCreateSubmit } from '@components/Projects/projectCreateBottomSheetSubmit';
import { ProjectCreateModalFooter } from '@components/Projects/ProjectCreateModalFooter';
import { ProjectCreateModalForm } from '@components/Projects/ProjectCreateModalForm';
import {
  getShowProjectLimitSnackBarNextTime,
  saveShowProjectLimitSnackBarNextTime,
} from '@components/Projects/ProjectForm.web';
import { Box } from '@components/Restyle';
import { TEXTFIELD_MULTILINE_DEFAULT_HEIGHT } from '@components/shared/TextField/TextField';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import {
  LocalFile,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import { useBottomSheetDiscard } from '@hooks/useBottomSheetDiscard';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useFilePickerHandlers } from '@hooks/useFilePickerHandlers';
import { useKeyboardHeight } from '@hooks/useKeyboardHeight';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import useSelectedMembers from '@hooks/useSelectedMembers';
import useTaskCreateFooter from '@hooks/useTaskCreateFooter';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { NEW_BOTTOM_SHEET_MAX_DOC_COUNT } from '@schemas/chatInputSchema';
import theme from '@themes/theme';
import { getTaskRoleEnumValue } from '@utils/tasks';

import { FormValues } from '../Projects/ProjectFormStack';

/**
 * Derives the description field's rendered height from the hidden measuring
 * Box's reported content height. That Box always measures one line taller
 * than the real content (it appends a trailing `\n` so a fresh blank line
 * registers as soon as the user presses enter), so this subtracts one line
 * back out and floors at a single line — the field's default (TA-10919 #2).
 */
export const clampDescriptionFieldHeight = (
  measuredContentHeight: number,
  lineHeight: number
): number => Math.max(lineHeight, measuredContentHeight - lineHeight);

/**
 * Transient fallback for the sheet's very first render, before the footer has
 * reported its real height via onLayout — visible for a single frame only,
 * then replaced. Picked to roughly match the footer's actual rendered height
 * (icon row + divider + team-selector/submit row) so that first frame
 * doesn't visibly jump once the real measurement lands (rule 20: not a theme
 * token because it approximates a composite, multi-row layout rather than
 * one spacing value — see HANDLE_HEIGHT below for a case where a single
 * token did apply).
 */
const DEFAULT_FOOTER_HEIGHT = 136;
/**
 * Transient floor for the sheet's very first render, before the name field
 * and footer have reported their real heights via onLayout — visible for a
 * single frame only. Deliberately well below any real sheet height so it
 * can only ever be replaced upward by the real measurement, never clip
 * content (rule 20: a floor chosen for safety margin, not a design value).
 */
const MIN_SHEET_HEIGHT = 320;
/**
 * Fixed vertical space the sheet reserves above the scrollable content for its
 * drag handle, empirically measured on-device (logging fitHeight alongside its
 * inputs across 1-6 description lines gave a consistent +76 offset unaccounted
 * for by content+footer+insets alone). Deliberately a flat literal, not a
 * theme-token composition (rule 20): the custom `handleComponent` below is
 * only a 4px bar with no margin, and `@gorhom/bottom-sheet`'s
 * `BottomSheetHandleContainer` adds no padding of its own around a *custom*
 * handle (its `styles.container.padding` only applies to gorhom's own default
 * handle, which this never renders) — so the other ~72px isn't attributable
 * to the handle at all. It's an opaque, measured compensation for whatever
 * else `fitHeight`'s inputs (content/footer/insets) don't account for, and a
 * token breakdown that merely reproduces 76 by arithmetic would misrepresent
 * it as derived when it isn't (dev review, PR #5939). Omitting it left short
 * content clipped; the wrong (smaller) value here instead left growing extra
 * empty space below the content as line count increased (TA-10919).
 */
const HANDLE_HEIGHT = 76;

/**
 * The height the sheet must be snapped to so its content sits flush above the
 * footer, with no blank band in between (TA-10919).
 *
 * `BottomSheetFooter` positions itself relative to the *container's* bottom
 * edge, not relative to the sheet's own top: @gorhom/bottom-sheet computes
 * `containerHeight - sheetPosition - keyboardHeight - footerHeight` (see
 * BottomSheetFooterContainer), which reduces to a constant on-screen Y once
 * `sheetPosition + sheetHeight === containerHeight`. Growing the sheet
 * therefore does NOT push the footer down — it only pushes the sheet's top
 * (and with it the name/description fields) UP, so every pixel of height added
 * beyond what the layout actually needs lands as blank space between the
 * content and the footer.
 *
 * `bottomReserve` is the space gorhom will keep free *below* the footer, and
 * is the only thing that differs between the keyboard-open and keyboard-closed
 * cases:
 *  - keyboard closed → `insets.bottom`, which the footer applies itself via
 *    its `bottomInset` prop.
 *  - keyboard open → the keyboard's height. gorhom explicitly *drops*
 *    `bottomInset` while the keyboard is shown (`BottomSheetFooter`:
 *    `if (keyboardStatus !== SHOWN) translateY -= bottomInset`) and pins the
 *    footer directly above the keyboard instead, so passing
 *    `keyboardHeight + insets.bottom` — or flooring at MIN_SHEET_HEIGHT —
 *    reserves room nothing fills.
 */
export const projectCreateSheetHeight = ({
  nameFieldBottom,
  descriptionFieldHeight,
  footerHeight,
  bottomReserve,
}: {
  nameFieldBottom: number;
  descriptionFieldHeight: number;
  footerHeight: number;
  bottomReserve: number;
}): number => {
  const height =
    nameFieldBottom +
    // The description TextInput actually renders at
    // `descriptionFieldHeight + theme.spacing.s` (ProjectCreateModalForm's
    // multiline style, TA-10919 #2) — match that here or every sheet is
    // under-sized by one `s` unit (Copilot review, PR #5939).
    descriptionFieldHeight +
    theme.spacing.s +
    HANDLE_HEIGHT +
    (footerHeight || DEFAULT_FOOTER_HEIGHT) +
    bottomReserve;
  // Before the name field and the footer have reported their real heights
  // there is nothing to fit to, so rest at a sane minimum rather than snapping
  // to a stub height and visibly growing a frame later. Once both are measured
  // the floor is dropped: it would otherwise pad the sheet out past its own
  // content, which is exactly the blank band this function exists to avoid.
  const measured = nameFieldBottom > 0 && footerHeight > 0;
  return measured ? height : Math.max(height, MIN_SHEET_HEIGHT);
};

export type ProjectCreateFormStackParamList = {
  'task-create-modal-form': undefined;
  'project-select': undefined;
  'member-select': undefined;
  'date-select': { field: FormValues['startDate' | 'dueDate'] };
  'dependency-select': undefined;
};

interface ProjectCreateBottomSheetComponentProps {
  parentProject?: boolean;
  values?: FormValues;
  loadingCreateProject?: boolean;
  /** Hands the caller a function that dismisses this sheet and resolves once
   * dismissed, so a mutation-success handler can sequence "close the sheet,
   * then navigate" instead of racing the sheet's own dismiss (TA-10919). */
  onDismissAfterNavigationReady?: (dismissFn: () => Promise<void>) => void;
}

const handleComponent = () => <Box style={styles.closeLine} />;

/**
 * iOS only. @gorhom/bottom-sheet keeps its own keyboard status, and it can be
 * left on SHOWN after the keyboard has hidden (seen about once in a dozen
 * dismissals; dev review, PR #5939) — most likely because
 * BottomSheetTextInput clears its focused target on blur by rewriting that
 * whole state from the JS thread, overwriting the hide that just landed.
 * Nothing corrects it afterwards, so the footer stays pinned where the
 * keyboard used to be: on this content-fit sheet, on top of the name and
 * description fields.
 *
 * The sheet already tracks the real keyboard height, so once that says the
 * keyboard is hidden, tell gorhom too: right away, and again when the
 * keyboard has finished hiding in case the stale write landed in between.
 */
const SyncHiddenKeyboardState = ({
  keyboardVisible,
}: {
  keyboardVisible: boolean;
}) => {
  const { animatedKeyboardState } = useBottomSheetInternal();

  useEffect(() => {
    if (Platform.OS !== 'ios' || keyboardVisible) return;
    const markHidden = () => {
      animatedKeyboardState.modify((state) => {
        'worklet';
        if (state.status === KEYBOARD_STATUS.SHOWN) {
          state.status = KEYBOARD_STATUS.HIDDEN;
        }
        return state;
      });
    };
    markHidden();
    const sub = Keyboard.addListener('keyboardDidHide', markHidden);
    return () => sub.remove();
  }, [keyboardVisible, animatedKeyboardState]);

  return null;
};

/**
 * Team Select Modal detent. Exactly one, on purpose (TA-11026): gorhom v5
 * lays a sheet's content out at its HIGHEST detent, so with several detents
 * the bottom of the team list sat below the screen whenever the sheet rested
 * on a lower one, and a list scrolled there parked its last rows off-screen
 * where they couldn't be tapped. With a single detent the content box is
 * always the visible area. Same approach as SelectModal's sheet (TA-10944).
 */
const TEAM_SELECT_SNAP_POINTS = ['70%'];

export const ProjectCreateBottomSheet = forwardRef(
  (
    {
      loadingCreateProject,
      onDismissAfterNavigationReady,
    }: ProjectCreateBottomSheetComponentProps,
    forwardRef
  ) => {
    const navigation = useNavigation();
    const isFocused = useIsFocused();
    const { t } = useTranslation();
    const insets = useSafeAreaInsets();
    const {
      isVisible,
      setProjectTutorialStateAndInStorage,
      projectTutorialState,
      addMembersTutorialState,
      setAddMemeberTutorialStateAndInStorage,
    } = useProjectTutorial();
    const { setSelectedMembers, selectedMembers, setShowTeamSelect } =
      useSelectedMembers();
    const [blankTaskForm, setBlankTaskForm] = useState(true);
    const { isReadySubmitting, setIsReadySubmitting } = useTaskCreateFooter();
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
    const [localImages, setLocalImages] = useState<LocalFile[]>([]);
    const [allFiles, setAllFiles] = useState<LocalFile[]>([]);
    const [descriptionContentHeight, setDescriptionContentHeight] = useState(0);

    const lastDescHeightRef = useRef(0);
    const updateDescriptionHeight = useCallback((h: number) => {
      if (!h || Number.isNaN(h)) return;
      if (Math.abs(h - lastDescHeightRef.current) < 6) return;
      lastDescHeightRef.current = h;
      setDescriptionContentHeight(h);
    }, []);

    const lastNameFieldBottomRef = useRef(0);
    const handleNameFieldBottomChange = useCallback((bottom: number) => {
      if (!bottom || Number.isNaN(bottom)) return;
      if (Math.abs(bottom - lastNameFieldBottomRef.current) < 4) return;
      lastNameFieldBottomRef.current = bottom;
      setNameFieldBottom(bottom);
    }, []);

    const updateMember = (userId: string, role: string) => {
      const members = selectedMembers?.map((u) => {
        if (u.id !== userId) return u;
        else {
          return { ...u, role: getTaskRoleEnumValue(role) };
        }
      });
      setSelectedMembers(members);
    };

    const {
      isValid,
      submitForm,
      resetForm,
      setFieldValue,
      isSubmitting,
      values,
    } = useFormikContext<FormValues>();

    const { teamId, name } = values;

    const { me } = useMe();
    const onboardingActive = useOnboardingActive();
    const [updateOnboardingState] = useUpdateOnboardingStateMutation();
    const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
    const sheetAnimatedPosition = useSharedValue(0);
    // refs
    const bottomSheetTaskFormRef = useRef<BottomSheetModal>(null);
    const bottomSheetTeamModalRef = useRef<BottomSheetModal>(null);
    useImperativeHandle(forwardRef, () => bottomSheetTaskFormRef.current);
    const bottomSheetProjectModalRef = useRef<BottomSheetModal>(null);

    const inputRef = useRef<TextInput>(null);
    // callbacks
    const pendingTooltipRef = React.useRef(false);
    const resolveSheetDismissedRef = useRef<(() => void) | null>(null);
    // ProjectCreateModalFooter hides its whole attachment-icons row for
    // onboarding projects (`!values.isOnboardingProject`), but
    // isOnboardingProject only flips true here, in this onChange handler,
    // AFTER the sheet has already snapped to its initial fitHeight — one
    // computed while the icon row still counted toward the footer's
    // measured height. enableDynamicSizing is false, so shrinking the
    // footer afterward doesn't revisit that snap point on its own, leaving
    // a gap below the content exactly the size of the now-hidden icon row
    // (TA-10919). This ref marks that a re-snap is owed once the footer's
    // own onLayout confirms the smaller height.
    const pendingOnboardingResnapRef = useRef(false);

    const handleFormSheetChange = (index: number) => {
      if (index === -1) {
        setShowOnboardingTooltip(false);
        pendingTooltipRef.current = false;
        const resolveSheetDismissed = resolveSheetDismissedRef.current;
        resolveSheetDismissedRef.current = null;
        if (resolveSheetDismissed) {
          // Dismissed programmatically via `dismissAndWaitForClose` (a
          // mutation-success handler) — that caller owns navigation once
          // this resolves, so don't also navigate from here.
          resolveSheetDismissed();
          return;
        }
        if (!blankTaskForm && !noNeedShowDiscard) {
          discardAlert();
        } else {
          navigation.goBack();
        }
      } else if (pendingTooltipRef.current) {
        pendingTooltipRef.current = false;
        setShowOnboardingTooltip(true);
        setFieldValue('isOnboardingProject', true);
        pendingOnboardingResnapRef.current = true;
      }
    };

    // Lets a mutation-success handler dismiss this sheet and wait for it to
    // actually close before navigating, instead of racing the sheet's own
    // dismiss animation (TA-10919 — a fixed-timer dismiss used to unmount
    // this screen before the create-project mutation had even resolved).
    const dismissAndWaitForClose = useCallback(() => {
      return new Promise<void>((resolve) => {
        resolveSheetDismissedRef.current = resolve;
        bottomSheetTaskFormRef.current?.dismiss();
      });
    }, []);

    useEffect(() => {
      onDismissAfterNavigationReady?.(dismissAndWaitForClose);
    }, [onDismissAfterNavigationReady, dismissAndWaitForClose]);
    const dismissTaskFormModal = () => {
      if (bottomSheetTaskFormRef.current) {
        bottomSheetTaskFormRef.current.dismiss();
      }
    };
    const dismissProjectModal = () => {
      setIsProjectModalOpen(false);
      if (bottomSheetProjectModalRef.current) {
        bottomSheetProjectModalRef.current.dismiss();
      }
    };

    useEffect(() => {
      if (
        onboardingActive &&
        me?.onboardingState?.projectCreationComplete === false
      ) {
        pendingTooltipRef.current = true;
        updateOnboardingState({
          variables: { attributes: { projectCreationComplete: true } },
          update: (cache) => {
            if (!me?.id) return;
            cache.modify({
              id: cache.identify({ __typename: 'User', id: me.id }),
              fields: {
                onboardingState: (existing) => ({
                  ...existing,
                  projectCreationComplete: true,
                }),
              },
            });
          },
        });
      }
    }, [onboardingActive, me?.onboardingState?.projectCreationComplete]);

    useEffect(() => {
      if (showOnboardingTooltip && name.length > 0) {
        setShowOnboardingTooltip(false);
      }
    }, [name, showOnboardingTooltip]);

    useEffect(() => {
      if (bottomSheetTaskFormRef.current) {
        bottomSheetTaskFormRef.current.present();
      }
    }, [bottomSheetTaskFormRef]);

    useEffect(() => {
      setFieldValue('users', selectedMembers);
    }, [selectedMembers]);

    useEffect(() => {
      if (localFiles.length > 0) {
        setFieldValue('files', localFiles);
      } else {
        setFieldValue('files', []);
      }
    }, [localFiles, setFieldValue]);

    const handleForm = (isFormBlank: boolean) => {
      setBlankTaskForm(isFormBlank);
    };

    const [noNeedShowDiscard, setNoNeedShowDiscard] = useState(false);

    // Reset noNeedShowDiscard when screen regains focus (e.g., after returning from add-members stack)
    useEffect(() => {
      if (isFocused) {
        setNoNeedShowDiscard(false);
      }
    }, [isFocused]);

    const childModals = useMemo(
      () => [{ isOpen: isProjectModalOpen, dismiss: dismissProjectModal }],
      [isProjectModalOpen, dismissProjectModal]
    );

    const { discardAlert, renderBackdrop } = useBottomSheetDiscard({
      bottomSheetRef:
        bottomSheetTaskFormRef as React.RefObject<BottomSheetModal>,
      isBlankForm: blankTaskForm,
      noNeedShowDiscard,
      isFocused,
      onDismiss: () => navigation.goBack(),
      titleKey: 'models:discardProject.title',
      messageKey: 'models:discardProject.message',
      childModals,
    });

    const _gotoLearnMore = useCallback(async () => {
      await Linking.openURL(
        'https://portal.tasktag.com/blog/how-do-i-upgrade-to-a-team-plan'
      );
    }, []);

    const [_showProjectLimitSnackBar, setShowProjectLimitSnackBar] =
      useState(false);
    const [_hideProjectLimitSnackBar, setHideProjectLimitSnackBar] =
      useState(false);
    const [lastSelectedTeamId, setLastSelectedTeamId] = useState<
      string | undefined
    >();

    const { teams } = useListTeamsFromQuery();
    const selectedTeam = teams.find((item) => item.id === teamId);
    // TA-9872: subscriptionDetails was split out of listTeams — read it from
    // the getTeamBilling query instead.
    const { billingByTeamId } = useTeamBillingFromQuery();
    const selectedTeamSubscriptionDetails =
      billingByTeamId[selectedTeam?.id ?? '']?.subscriptionDetails;
    // Billing loads asynchronously (TA-9872); until it has resolved for the
    // selected team, isActiveOrTrialingTeam reads false and the snackbar would
    // flash. Gate the effect below on this.
    const billingReadyForSelected =
      !selectedTeam || billingByTeamId[selectedTeam.id] !== undefined;

    const isActiveOrTrialingTeam =
      selectedTeamSubscriptionDetails?.subscriptionPlanName === 'Team' &&
      (selectedTeamSubscriptionDetails?.subscriptionStatus === 'trialing' ||
        selectedTeamSubscriptionDetails?.subscriptionStatus === 'active');

    const projectLeft =
      (selectedTeam?.maxNumberOfProjects ?? 0) -
      (selectedTeam?.numberOfProjectsUsed ?? 0);

    const needShowProjectLimitSnackBar = useCallback(async () => {
      const savedTimes = await getShowProjectLimitSnackBarNextTime();
      const savedTime = savedTimes?.find(
        (item) => item.userId === me?.id && item.teamId === selectedTeam?.id
      );
      if (savedTime) {
        if (dayjs(Date()).diff(new Date(savedTime.nextTime)) < 0) {
          return;
        }
      }
    }, [me?.id, selectedTeam?.id]);

    useEffect(() => {
      // Wait for billing before deciding snackbar visibility — otherwise the
      // selected team reads as non-active and the nudge flashes incorrectly.
      if (!billingReadyForSelected) return;
      if (
        !isActiveOrTrialingTeam &&
        selectedTeam?.maxNumberOfProjects &&
        selectedTeam?.id !== lastSelectedTeamId
      ) {
        if (projectLeft < 1 && selectedTeam.maxNumberOfProjects > 0) {
          setShowProjectLimitSnackBar(true);
          setHideProjectLimitSnackBar(false);
        } else if (
          projectLeft === 1 ||
          (projectLeft === 2 && selectedTeam.maxNumberOfProjects > 0)
        ) {
          needShowProjectLimitSnackBar();
        } else {
          setShowProjectLimitSnackBar(false);
          setHideProjectLimitSnackBar(true);
        }
      }
      // isActiveOrTrialingTeam derives from billingByTeamId, which resolves
      // independently of selectedTeam (TA-9872). Include it (and the readiness
      // flag) so the snackbar re-evaluates when billing arrives instead of
      // staying stale.
    }, [selectedTeam, isActiveOrTrialingTeam, billingReadyForSelected]);

    const _onCloseProjectUpgradeModal = () => {
      setLastSelectedTeamId(teamId);
      setShowProjectLimitSnackBar(false);
      setHideProjectLimitSnackBar(true);
      saveShowProjectLimitSnackBarNextTime(me?.id, selectedTeam?.id);
    };

    const [bottomSheetFooterHeight, setBottomSheetFooterHeight] =
      useState<number>(0);
    // Bottom offset of the name field (from the top of the form) so the
    // sheet can size itself to fit the visible name+description fields
    // instead of always resting at a fixed 70-85% of the screen, which left
    // a large empty gap below short content (TA-10919). The name field
    // never grows (always single-line), so this plus descriptionFieldHeight
    // below gives an accurate, cheap content estimate without the risk of
    // measuring a subtree that contains a `flex: 1` descendant (which
    // stretches to fill the ScrollView instead of reporting its true size).
    const [nameFieldBottom, setNameFieldBottom] = useState<number>(0);

    const { keyboardHeight } = useKeyboardHeight();

    const footerBottomInset = useMemo(
      () =>
        keyboardHeight > 0 ? keyboardHeight + insets.bottom : insets.bottom,
      [keyboardHeight, insets.bottom]
    );

    const { uploadingFiles, setUploadingFiles, directUploadFiles } =
      useChatUploadManager();

    const handleUploadAndUpdate = async (
      filesToUpload: LocalFile[],
      isImage: boolean
    ) => {
      try {
        const uploaded = await directUploadFiles(filesToUpload);
        (isImage ? setLocalImages : setLocalFiles)((prev) => {
          const map = new Map(prev.map((f) => [f.clientId, f]));
          uploaded.forEach((item) => {
            if (map.has(item.clientId)) {
              const original = map.get(item.clientId)!;
              map.set(item.clientId, {
                ...original,
                blobId: item.blobId,
              });
            }
          });
          return Array.from(map.values());
        });
      } catch (error) {
        console.error('Error uploading files:', error);
      }
    };

    const showLimitReached = () => {
      Alert.alert('Limit Reached', t('models:chat.input.maxSelection64'), [
        {
          text: 'Got It',
          style: 'destructive',
          onPress: () => {},
        },
      ]);
    };

    const { pickFiles, pickImages, launchCamera } = useFilePickerHandlers({
      allFilesCount: allFiles?.length ?? 0,
      maxCount: NEW_BOTTOM_SHEET_MAX_DOC_COUNT,
      setNoNeedShowDiscard,
      setLocalFiles,
      setLocalImages,
      handleUploadAndUpdate,
      navigation,
      showLimitReached,
    });

    useEffect(() => {
      setSelectedMembers([]);
      setUploadingFiles([]);
      setLocalFiles([]);
      setIsReadySubmitting(false);
      isSubmittingTask.current = false;
    }, []);

    useEffect(() => {
      const allFiles = [...localFiles, ...localImages];
      allFiles && setAllFiles(allFiles);
      allFiles && setFieldValue('files', allFiles);
    }, [localFiles, localImages]);

    // Scope uploadingFiles to this task's files using clientId matching
    const taskClientIds = useMemo(
      () => new Set((allFiles ?? []).map((f) => f.clientId).filter(Boolean)),
      [allFiles]
    );

    const taskUploadingFiles = useMemo(
      () => uploadingFiles.filter((u) => taskClientIds.has(u.file.clientId)),
      [uploadingFiles, taskClientIds]
    );

    // Use uploadingFiles for incremental progress tracking
    // A file is considered "uploaded" when it has a blobId set (even if status is still 'uploading')
    const fileStillUploading = useMemo(
      () => taskUploadingFiles.filter((u) => u.status === 'uploading'),
      [taskUploadingFiles]
    );

    const fileUploadedCount = useMemo(() => {
      const total = taskUploadingFiles.reduce((sum, item) => {
        return sum + (Number(item.progress) || 0);
      }, 0);

      return Math.floor(total);
    }, [taskUploadingFiles]);

    const totalFiles = allFiles?.length ?? 0;

    // Show loader when user has clicked submit and there are files still uploading
    const shouldShowLoader =
      isReadySubmitting && totalFiles > 0 && fileStillUploading.length > 0;

    const isSubmittingTask = useRef<boolean>(false);

    useEffect(() => {
      const uploadingFilesFailed = taskUploadingFiles.filter(
        (u) => u.status === 'error'
      );
      if (uploadingFilesFailed && uploadingFilesFailed.length > 0) {
        setIsReadySubmitting(false);
        Alert.alert(
          'TaskTag',
          t('models:files.uploadFilesError1', {
            count: uploadingFilesFailed.length,
          })
        );
      }
    }, [taskUploadingFiles]);

    useEffect(() => {
      // Only proceed if user has clicked submit and there are files
      if (!isReadySubmitting) {
        return;
      }

      const notHaveBlobIdFiles = allFiles.filter(
        (file) => !file.blobId || file.blobId === ''
      );

      const uploadingFilesFailed = taskUploadingFiles.filter(
        (u) => u.status === 'error'
      );

      if (
        allFiles.length > 0 &&
        notHaveBlobIdFiles.length !== uploadingFilesFailed.length
      ) {
        return;
      }

      if (fileStillUploading.length === 0 && !isSubmittingTask.current) {
        // All files for this task have finished uploading - submit the form
        allFiles &&
          allFiles.length > 0 &&
          (!values.files || values.files.length > 0) &&
          setFieldValue('files', allFiles);

        setTimeout(
          () => {
            isSubmittingTask.current = true;
            runProjectCreateSubmit(submitForm, {
              onSuccess: () => {
                setTimeout(() => {
                  setSelectedMembers([]);
                  setLocalFiles([]);
                  setLocalImages([]);
                  setAllFiles([]);
                  setBlankTaskForm(true);
                  resetForm();
                  setIsReadySubmitting(false);
                  isSubmittingTask.current = false;
                }, 500);
              },
              onFailure: () => {
                // The submit failed (e.g. offline — ProjectFormStack shows
                // the error and rejects). Skip the cleanup above so the
                // entered name/members/attachments survive, but release the
                // submit lock so the user can retry.
                setIsReadySubmitting(false);
                isSubmittingTask.current = false;
              },
            });
          },
          500 * ((allFiles.length > 20 ? allFiles.length : 20) / 20)
        );
      }
    }, [allFiles, isReadySubmitting, fileStillUploading, taskUploadingFiles]);

    const getProjectCreateModalFooter = () => (
      <ProjectCreateModalFooter
        dismissProjectModal={dismissProjectModal}
        handleTeamSelectorPress={handleTeamSelectorPress}
        members={selectedMembers}
        onFormValidityChange={handleForm}
        onSave={() => {
          setIsReadySubmitting(true);
          isSubmittingTask.current = false;
        }}
        setNoNeedShowDiscard={setNoNeedShowDiscard}
        onCancel={() => {
          setSelectedMembers([]);
          dismissTaskFormModal();
        }}
        updateMember={(member, role) => updateMember(member.id, role)}
        addMembers={() => {
          if (isVisible && addMembersTutorialState !== '4') {
            setAddMemeberTutorialStateAndInStorage('1');
          }
          navigation.navigate('project-add-members-stack', {
            selectedTeamId: teamId,
            isCreateProjectForMobile: true,
            memberCount: selectedMembers.length ?? 0,
          });
        }}
        pickFiles={pickFiles}
        pickImages={pickImages}
        launchCamera={launchCamera}
      />
    );

    const renderFooter = useCallback(
      (props: BottomSheetFooterProps) => {
        return (
          <BottomSheetFooter
            bottomInset={footerBottomInset}
            style={{
              backgroundColor: 'white',
            }}
            {...props}>
            <Box
              onLayout={(e) => {
                setBottomSheetFooterHeight(e.nativeEvent.layout.height);
              }}>
              {getProjectCreateModalFooter()}
            </Box>
            {isVisible && projectTutorialState === '4' && (
              <Box
                zIndex={10}
                position='absolute'
                bottom={136}
                left={46}
                right={16}>
                <TooltipPopup
                  visible={true}
                  title='Add Members'
                  description='Add/Invite members to collaborate on this project. You will be able to add later.'
                  pointerPosition='bottom-right'
                  onSkip={() => saveProjectTutorializationState('5')}
                  currentStep={3}
                  totalSteps={4}
                  maxWidth={Dimensions.get('window').width}
                  bottomRightPos={16}
                />
              </Box>
            )}
            {isVisible && projectTutorialState === '5' && (
              <Box
                zIndex={10}
                alignSelf='flex-end'
                position='absolute'
                left={46}
                right={16}
                bottom={70}>
                <TooltipPopup
                  visible={true}
                  title='Create Project'
                  description='All set! You can view the Project details now'
                  pointerPosition='bottom-right'
                  currentStep={4}
                  totalSteps={4}
                  maxWidth={Dimensions.get('window').width}
                  bottomRightPos={16}
                />
              </Box>
            )}
          </BottomSheetFooter>
        );
      },
      [
        footerBottomInset,
        selectedMembers,
        isVisible,
        projectTutorialState,
        addMembersTutorialState,
      ]
    );

    const dismissTeamSelectModal = useCallback(() => {
      if (bottomSheetTeamModalRef.current) {
        bottomSheetTeamModalRef.current.dismiss();
      }
    }, []);

    const openTeamSelectModal = useCallback(() => {
      if (bottomSheetTeamModalRef.current) {
        Keyboard.dismiss();
        if (name.length > 0) {
          setShowOnboardingTooltip(false);
        }
        bottomSheetTeamModalRef.current.present();
      }
    }, [name]);

    const handleTeamSelectorPress = useCallback(() => {
      setShowTeamSelect(true);
      openTeamSelectModal();
    }, [setShowTeamSelect, openTeamSelectModal]);

    // Imported from TextField itself (rule 20, dev review PR #5939) rather
    // than re-hardcoding `24` here with a comment promising to keep it in
    // sync — if TextField's default single-line height ever changes, this
    // now changes with it automatically instead of silently drifting.
    const DESCRIPTION_LINE_HEIGHT = TEXTFIELD_MULTILINE_DEFAULT_HEIGHT;

    // The hidden measuring Box in ProjectCreateModalForm renders
    // `description + '\n'`, so its measured height is always one row taller
    // than the real content (an empty description already measures 2 rows).
    // Subtract that row back out so the field itself defaults to a true
    // single line (TA-10919 #2) and only grows once real content wraps.
    const descriptionFieldHeight = useMemo(
      () =>
        clampDescriptionFieldHeight(
          descriptionContentHeight,
          DESCRIPTION_LINE_HEIGHT
        ),
      [descriptionContentHeight]
    );

    // A fixed 70-90% snap point left a large empty gap below short content
    // (e.g. just name+description during onboarding, TA-10919). Size the
    // resting snap point to the actual measured content instead, still
    // capped at a max so it can grow for attachments/members.
    const fitHeight = useMemo(
      () =>
        projectCreateSheetHeight({
          nameFieldBottom,
          descriptionFieldHeight,
          footerHeight: bottomSheetFooterHeight,
          // Keyboard closed: the footer lifts itself by its own `bottomInset`
          // (= insets.bottom), so the sheet has to be that much taller.
          bottomReserve: insets.bottom,
        }),
      [
        nameFieldBottom,
        descriptionFieldHeight,
        bottomSheetFooterHeight,
        insets.bottom,
      ]
    );

    // The same content-fit height, for while the keyboard is open.
    const keyboardOpenHeight = useMemo(
      () =>
        projectCreateSheetHeight({
          nameFieldBottom,
          descriptionFieldHeight,
          footerHeight: bottomSheetFooterHeight,
          // Keyboard open: gorhom pins the footer directly above the keyboard
          // and ignores its `bottomInset`, so the keyboard's height is the
          // whole reserve — adding insets.bottom on top of it (or flooring at
          // MIN_SHEET_HEIGHT) only buys blank space between the description
          // field and the footer, which is the band onboarding exposed once it
          // stopped rendering the attachment icon row (TA-10919).
          bottomReserve: keyboardHeight,
        }),
      [
        nameFieldBottom,
        descriptionFieldHeight,
        bottomSheetFooterHeight,
        keyboardHeight,
      ]
    );

    const maxSheetHeight = useMemo(() => {
      const screenHeight = Dimensions.get('window').height;
      const isTutorialStep3 = isVisible && projectTutorialState === '3';
      let maxHeightPercent = 0.9;
      if (Platform.OS === 'android') {
        maxHeightPercent = isTutorialStep3 ? 0.7 : 0.85;
      }
      return screenHeight * maxHeightPercent;
    }, [isVisible, projectTutorialState]);

    // iOS keeps keyboardBehavior='extend', which always moves the sheet to
    // its TALLEST snap point while the keyboard is shown. With a tall
    // max-height fallback configured that ballooned the sheet to 90% of the
    // screen the instant the autofocused name field opened the keyboard, and
    // left it stuck there once the keyboard hid (dev review, PR #5939). So on
    // iOS every snap point IS the one height the sheet should have right now
    // — content-fit, or content-fit above the keyboard — and 'extend' can
    // only ever land on it, whatever gorhom believes the keyboard is doing
    // (see SyncHiddenKeyboardState).
    //
    // Android's manual snapToPosition (below) can't be reused here: gorhom
    // resolves a position that isn't a snap point to index -1 ("closed")
    // unless its own keyboard state is already SHOWN, and on iOS that state
    // lags keyboardWillShow until the focused input's onFocus lands — on a
    // cold start the sheet silently closed itself right after opening.
    // 0 on Android.
    let iosSheetHeight = 0;
    if (Platform.OS === 'ios') {
      // Attachment previews render below the description, outside what the
      // content-fit estimate measures — a content-fit sheet clips them behind
      // the footer. With any attached, fall back to the max height iOS always
      // used to sit at.
      iosSheetHeight =
        allFiles.length > 0
          ? maxSheetHeight
          : Math.min(
              keyboardHeight > 0 ? keyboardOpenHeight : fitHeight,
              maxSheetHeight
            );
    }

    const initialSnapPoints = useMemo(
      () =>
        iosSheetHeight > 0
          ? [iosSheetHeight, iosSheetHeight]
          : [Math.min(fitHeight, maxSheetHeight), maxSheetHeight],
      [iosSheetHeight, fitHeight, maxSheetHeight]
    );

    const snapPointsRef = useRef(initialSnapPoints);
    useEffect(() => {
      snapPointsRef.current = initialSnapPoints;
    }, [initialSnapPoints]);

    // Consumes pendingOnboardingResnapRef: once the footer's own onLayout
    // reports its new, smaller height (icon row now hidden) and fitHeight/
    // initialSnapPoints have recomputed to match, settle the sheet at the
    // now-correct content-fit index instead of leaving it at the taller
    // snap point computed before isOnboardingProject flipped (TA-10919).
    // The Android manual keyboard-positioning effect below already reacts to
    // bottomSheetFooterHeight and re-snaps to keyboardOpenHeight on its own
    // whenever the keyboard is open, so only handle the
    // keyboard-closed case here — duplicating its snapToPosition call would
    // just repeat the same result a second time.
    useEffect(() => {
      if (!pendingOnboardingResnapRef.current) return;
      pendingOnboardingResnapRef.current = false;
      if (Platform.OS !== 'android' || keyboardHeight <= 0) {
        bottomSheetTaskFormRef.current?.snapToIndex(0);
      }
    }, [bottomSheetFooterHeight]);

    // @gorhom/bottom-sheet's 'interactive' keyboardBehavior always jumps to
    // the TALLEST configured snap point while the keyboard is shown — by
    // design, regardless of the current/resting one — which reintroduced the
    // large empty gap the instant the keyboard opened (TA-10919). Android
    // uses keyboardBehavior='none' below and this manually positions the
    // sheet at content-fit height plus the keyboard's own height instead, so
    // the keyboard itself (drawn on top) covers the extra padding rather
    // than the sheet visibly ballooning to its max.
    useEffect(() => {
      if (Platform.OS !== 'android') return;
      if (keyboardHeight <= 0) return;
      // duration: 0 — fast/continuous typing re-fires this on every content
      // growth; an animated transition can't finish before the next call
      // replaces its target, so the sheet perpetually lags behind the
      // actually-growing content and clips it (TA-10919). Snapping instantly
      // keeps it always caught up.
      bottomSheetTaskFormRef.current?.snapToPosition(keyboardOpenHeight, {
        duration: 0,
      });
      // keyboardOpenHeight tracks nameFieldBottom/descriptionFieldHeight/
      // footerHeight directly (and, once they are measured, is no longer
      // floored), so it changes on every real content change and this effect
      // re-fires with it — the sheet never lags behind growing content.
    }, [keyboardHeight, keyboardOpenHeight]);

    const tooltipAnimatedStyle = useAnimatedStyle(() => ({
      transform: [{ translateY: sheetAnimatedPosition.value }],
    }));
    const TOOLTIP_HIDE_DISTANCE = theme.spacing.xl + theme.spacing.xxs;
    const screenHeight = Dimensions.get('window').height;
    useAnimatedReaction(
      () => sheetAnimatedPosition.value,
      (position) => {
        if (position >= screenHeight - TOOLTIP_HIDE_DISTANCE) {
          runOnJS(setShowOnboardingTooltip)(false);
        }
      }
    );

    // Settle back to the content-fit snap point (index 0) once the keyboard
    // hides. This used to jump to the LAST index, which made sense back
    // when initialSnapPoints was a plain percentage pair (e.g. ['70%',
    // '85%']) — but TA-10919's content-fit sizing redefined index 0 as the
    // fit-height point and the last index as the max-height fallback, so
    // this was forcing the sheet back to max height (a large empty gap
    // below short content) every time the keyboard dismissed — via the
    // back button, tapping outside the field, or blur.
    useEffect(() => {
      if (Platform.OS !== 'android') return;
      const sub = Keyboard.addListener('keyboardDidHide', () => {
        bottomSheetTaskFormRef.current?.snapToIndex(0);
      });
      return () => sub.remove();
    }, []);

    useEffect(() => {
      if (Platform.OS !== 'android') return;
      if (allFiles.length > 0) {
        bottomSheetTaskFormRef.current?.snapToIndex(
          snapPointsRef.current.length - 1
        );
      }
    }, [allFiles.length]);

    const saveProjectTutorializationState = useCallback(
      async (currentState: string) => {
        setProjectTutorialStateAndInStorage(currentState);
      },
      [setProjectTutorialStateAndInStorage]
    );

    const getBottomSheetView = () => {
      const projectCreateModalForm = loadingCreateProject ? (
        <ActivityIndicator />
      ) : (
        <>
          <ProjectCreateModalForm
            selectedTeam={selectedTeam}
            isSubmitting={isSubmitting}
            bottomSheetTaskFormRef={bottomSheetTaskFormRef}
            textInputRef={inputRef}
            members={selectedMembers}
            onFormValidityChange={handleForm}
            localFiles={localFiles}
            setLocalFiles={setLocalFiles}
            localImages={localImages}
            setLocalImages={setLocalImages}
            disabled={!isValid || isSubmitting}
            shouldShowLoader={shouldShowLoader}
            fileUploadedCount={fileUploadedCount}
            onCancel={() => {
              setSelectedMembers([]);
              dismissTaskFormModal();
            }}
            isReadySubmitting={isReadySubmitting}
            onDescriptionContentSizeChange={updateDescriptionHeight}
            descriptionFieldHeight={descriptionFieldHeight}
            onNameFieldBottomChange={handleNameFieldBottomChange}
          />
        </>
      );
      if (Platform.OS === 'ios') {
        return (
          <BottomSheetScrollView
            keyboardShouldPersistTaps='handled'
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: (bottomSheetFooterHeight ?? 136) + insets.bottom,
            }}>
            {projectCreateModalForm}
          </BottomSheetScrollView>
        );
      } else {
        return (
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps='handled'
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: (bottomSheetFooterHeight ?? 136) + insets.bottom,
            }}>
            {projectCreateModalForm}
          </ScrollView>
        );
      }
    };

    return (
      <>
        <BottomSheetModalProvider>
          <Animated.View
            pointerEvents='box-none'
            style={[
              {
                position: 'absolute',
                left: theme.spacing.l,
                top:
                  Platform.OS === 'android'
                    ? theme.spacing.m + theme.spacing.listFooter
                    : -theme.spacing.l,
                zIndex: 99999,
              },
              tooltipAnimatedStyle,
            ]}>
            <TooltipPopup
              isFromChat
              animate={true}
              fadeTime={2000}
              visible={onboardingActive && showOnboardingTooltip}
              title={t('models:projects.tooltip.nameTitle')}
              description={t('models:projects.tooltip.nameSubtitle')}
              pointerPosition={
                Platform.OS === 'android' ? 'top-left' : 'bottom-left'
              }
            />
          </Animated.View>

          <BottomSheetModal
            name='Project Form'
            index={0}
            enablePanDownToClose
            ref={bottomSheetTaskFormRef}
            snapPoints={initialSnapPoints}
            maxDynamicContentSize={800}
            // 'extend'/'interactive' both jump the sheet to its TALLEST
            // configured snap point the instant the keyboard shows — by
            // design, regardless of the current/resting one — reintroducing
            // the large empty gap below short content (TA-10919). Android
            // instead uses 'none' and the effect above manually positions
            // the sheet at content-fit height + keyboard height. iOS keeps
            // 'extend' but makes every snap point the content-fit one (see
            // initialSnapPoints).
            keyboardBehavior={Platform.OS === 'ios' ? 'extend' : 'none'}
            keyboardBlurBehavior={Platform.OS === 'ios' ? 'restore' : 'none'}
            enableDynamicSizing={false}
            enableContentPanningGesture={Platform.OS !== 'android'}
            handleComponent={handleComponent}
            footerComponent={renderFooter}
            backdropComponent={renderBackdrop}
            animatedPosition={sheetAnimatedPosition}
            onChange={handleFormSheetChange}>
            <SyncHiddenKeyboardState keyboardVisible={keyboardHeight > 0} />
            {getBottomSheetView()}
          </BottomSheetModal>
          <BottomSheetModal
            android_keyboardInputMode='adjustPan'
            name='Team Select Modal'
            index={0}
            stackBehavior='push'
            ref={bottomSheetTeamModalRef}
            enablePanDownToClose
            keyboardBehavior={Platform.OS === 'ios' ? 'extend' : 'interactive'}
            keyboardBlurBehavior='restore'
            enableDynamicSizing={false}
            snapPoints={TEAM_SELECT_SNAP_POINTS}
            backdropComponent={BottomSheetBackdrop}
            handleComponent={handleComponent}>
            <>
              <FloatingHeader
                title={t('models:projects.create.teams.title')}
                simple
                hideBorder
                onCancel={dismissTeamSelectModal}
                onSave={dismissTeamSelectModal}
                saveButtonTitle='Done'
                disabled
              />
              <TeamSelect
                isBottomSheet
                onSelectFinish={dismissTeamSelectModal}
              />
            </>
          </BottomSheetModal>
        </BottomSheetModalProvider>
      </>
    );
  }
);

const styles = StyleSheet.create({
  closeLine: {
    alignSelf: 'center',
    width: 56,
    height: 4,
    borderRadius: 2.5,
    backgroundColor: theme.colors.grey03,
    margin: theme.spacing.l,
  },

  closeLineScreen: {
    margin: theme.spacing.l,
  },
  snackBarContainer: {
    padding: theme.spacing.m,
    backgroundColor: 'black',
    borderTopEndRadius: theme.borderRadii.s,
    borderTopStartRadius: theme.borderRadii.s,
    flexDirection: 'column',
  },
  snackBarOuterContainer: {
    flex: 1,
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
});
